import React, { useCallback, useRef, useState } from 'react';
import { FiPlus, FiTrash2 } from 'react-icons/fi';
import { CircleSpinner } from 'react-spinners-kit';
import styled from 'styled-components';
import { apiConfigs } from '../../../../api/APIConfigs';
import { ModuleType } from '../../../../api/models/BibleStudyModule';
import { BookAltSvg } from '../../../../assets/svg-icon-components/BookAltSvg';
import { EditSvg } from '../../../../assets/svg-icon-components/EditSvg';
import { FileMusicSvg } from '../../../../assets/svg-icon-components/FileMusicSvg';
import { PictureSvg } from '../../../../assets/svg-icon-components/PictureSvg';
import { PlayAltSvg } from '../../../../assets/svg-icon-components/PlayAltSvg';
import { TextSvg } from '../../../../assets/svg-icon-components/TextSvg';
import { useAsyncRequest } from '../../../../util/use-async-request';
import { useRequest } from '../../../../util/use-request';
import { HorizontalContent } from '../../../core/HorizontalContent';
import { RhButton, RhButtonMode } from '../../../core/RhButton';
import { RhInput, RhInputMode } from '../../../core/RhInput';
import { RhModal } from '../../../core/RhModal';
import { RhSelect, RhSelectOption } from '../../../core/RhSelect';
import { RhTextArea } from '../../../core/RhTextArea';
import { SizedBox } from '../../../core/SizedBox';
import { colors, Description, H2, H3 } from '../../../core/Styles';
import { ModuleFormState } from '../../bible-study-form';
import { ScriptureModuleForm } from './ScriptureModuleForm';

type Props = {
  /** if modal to add new module is open or not */
  isModuleModalOpen: boolean;
  /** form for the section module */
  moduleForm: ModuleFormState;
  /** some form of error for the module modal */
  moduleFormError: string | null;
  /** if file upload on module form modal is currently taking place */
  isModuleFormFileUploading: boolean;
  /**
   * keys for input[type=file] for resetting value
   * see: https://github.com/redux-form/redux-form/issues/769
   */
  fileInputKeys: { image: string; video: string; scripture: string; audio: string };
  isLoading?: boolean;
  /** open or close module modal */
  userToggledModuleModal: () => void;
  /** user wants to reset the module form */
  userResetModuleForm: () => void;
  /** called when the user edits the module fields */
  userUpdatedModuleForm: (formState: ModuleFormState) => void;
  /** user chooses a module file (video or image) */
  userChoseModuleFile: (e: React.ChangeEvent<HTMLInputElement>) => void;
  /** user submits the module form */
  userSubmittedModuleForm: () => void;
  /** user wants to delete module file on form */
  userClickedDeleteModuleFormFile: () => void;
};

type ModuleButton = {
  id: string;
  type: ModuleType;
  icon: React.FC<React.SVGProps<SVGSVGElement>>;
  title: string;
  callback?: () => void;
};

/**
 * Module form.
 * @deprecated In favor of JiralikeContentModal
 */
export const ModuleModal: React.FC<Props> = props => {
  const { type } = props.moduleForm;

  const request = useRequest();

  const imageModuleRef = useRef<HTMLInputElement>(null);
  const videoModuleRef = useRef<HTMLInputElement>(null);
  const audioModuleRef = useRef<HTMLInputElement>(null);

  const [versionOptions, setVersionOptions] = useState<RhSelectOption[]>([]);

  type BibleVersion = { id: string; value: string; name: string };

  // Load bible versions on mount
  const { loading: loadingVersions } = useAsyncRequest(
    useCallback(() => request<BibleVersion[]>(apiConfigs.getBibleVersions()), [request]),
    useCallback(result => {
      const versions: BibleVersion[] = result || [];
      setVersionOptions(
        versions.map(v => ({
          label: v.name,
          value: v.id,
        }))
      );
    }, [])
  );

  const moduleButtons: ModuleButton[] = [
    { id: '1', type: 'video', icon: PlayAltSvg, title: 'Video', callback: () => videoModuleRef.current?.click() },
    { id: '2', type: 'image', icon: PictureSvg, title: 'Image', callback: () => imageModuleRef.current?.click() },
    { id: '3', type: 'scripture', icon: BookAltSvg, title: 'Scripture' },
    { id: '5', type: 'qanda', icon: EditSvg, title: 'Q&A' },
    { id: '6', type: 'audio', icon: FileMusicSvg, title: 'Audio', callback: () => audioModuleRef.current?.click() },
    { id: '7', type: 'text', icon: TextSvg, title: 'Text', callback: () => {} },
  ];

  /**
   * Renders a scripture module form
   * Scripture forms can be present in both scripture and Q&A module
   */
  const renderScriptureModuleForm = () => {
    return (
      <>
        <AlignRight>
          <RhButton
            mode={RhButtonMode.subtle}
            prefixIcon={FiPlus}
            iconSize={18}
            onClick={() => {
              const newGroupOfScriptures = [...(props.moduleForm.scriptures || [])];
              newGroupOfScriptures.push({ id: `${newGroupOfScriptures.length + 1}`, scriptureIds: [] });
              props.userUpdatedModuleForm({ ...props.moduleForm, scriptures: newGroupOfScriptures });
            }}
          />
        </AlignRight>
        <H2>Version</H2>
        <SizedBox height={8} />
        <RhSelect
          value={versionOptions.find(opt => opt.value === props.moduleForm.translationId) || null}
          onChange={e => {
            props.userUpdatedModuleForm({ ...props.moduleForm, translationId: e.value });
          }}
          isLoading={loadingVersions}
          isDisabled={loadingVersions}
          options={versionOptions}
        />
        <SizedBox height={20} />
        <Scriptures>
          {props.moduleForm.scriptures?.map((scripture, scripturesIndex) => (
            <ScriptureModuleForm
              key={scripture.id}
              translationId={props.moduleForm.translationId}
              onResolvedTranslationId={resolvedId =>
                props.userUpdatedModuleForm({ ...props.moduleForm, translationId: resolvedId })
              }
              initialForm={scripture.initialScriptureForm}
              isDeleteButtonHidden={!((props.moduleForm.scriptures?.length || 0) > 1)}
              onChange={scriptureIds => {
                const newGroupOfScriptures = [...(props.moduleForm.scriptures || [])].map((existingScriptures, i) =>
                  scripturesIndex === i ? { ...existingScriptures, scriptureIds } : existingScriptures
                );
                props.userUpdatedModuleForm({ ...props.moduleForm, scriptures: newGroupOfScriptures });
              }}
              onDeleteButtonClick={() => {
                const newGroupOfScriptures = [...(props.moduleForm.scriptures || [])];
                newGroupOfScriptures.splice(scripturesIndex, 1);
                props.userUpdatedModuleForm({ ...props.moduleForm, scriptures: newGroupOfScriptures });
              }}
            />
          ))}
        </Scriptures>
      </>
    );
  };

  /**
   * The main render of this component
   */
  return (
    <RhModal
      isOpen={props.isModuleModalOpen}
      contentStyle={type === 'scripture' || type === 'qanda' ? { width: '50%', maxHeight: '80vh' } : {}}
      onRequestClose={() => {
        props.userToggledModuleModal();
        props.userResetModuleForm();
      }}>
      <HorizontalContent
        left={<H3>New Content Module</H3>}
        right={props.isLoading && <CircleSpinner key="loader" size={15} color={colors.primary} />}
      />
      <SizedBox height={10} />
      <RhTextArea
        disabled={props.isLoading || props.isModuleFormFileUploading}
        value={props.moduleForm.title}
        mode={RhInputMode.subtle}
        placeholder="Title..."
        onChange={e => props.userUpdatedModuleForm({ ...props.moduleForm, title: e.target.value })}
      />
      <SizedBox height={20} />
      <ModuleButtons>
        {moduleButtons.map(item => (
          <RhButton
            key={item.id}
            mode={type === item.type ? RhButtonMode.primary : RhButtonMode.secondary}
            disabled={props.isLoading || props.isModuleFormFileUploading}
            prefixSvg={item.icon}
            title={item.title}
            onClick={() => {
              props.userUpdatedModuleForm({ ...props.moduleForm, type: item.type });
              if (item.callback) {
                item.callback();
              }
            }}
          />
        ))}
      </ModuleButtons>
      <input
        key={props.fileInputKeys.image}
        type="file"
        hidden={true}
        accept="image/*"
        multiple={false}
        ref={imageModuleRef}
        onChange={props.userChoseModuleFile}
      />
      <input
        key={props.fileInputKeys.video}
        type="file"
        hidden={true}
        accept="video/*"
        multiple={false}
        ref={videoModuleRef}
        onChange={props.userChoseModuleFile}
      />
      <input
        key={props.fileInputKeys.audio}
        type="file"
        hidden={true}
        accept="audio/*"
        multiple={false}
        ref={audioModuleRef}
        onChange={props.userChoseModuleFile}
      />
      {type === 'scripture' && (
        <>
          <SizedBox height={8} />
          {renderScriptureModuleForm()}
        </>
      )}
      {type === 'qanda' && (
        <>
          <SizedBox height={8} />
          <AlignRight>
            <RhButton
              mode={RhButtonMode.subtle}
              prefixIcon={FiPlus}
              iconSize={18}
              onClick={() => {
                props.userUpdatedModuleForm({
                  ...props.moduleForm,
                  questionAndAnswers: [...(props.moduleForm.questionAndAnswers || []), { question: '', answer: '' }],
                });
              }}
            />
          </AlignRight>
          <SizedBox height={10} />
          <Questions>
            {props.moduleForm.questionAndAnswers?.map((questionAndAnswer, questionIndex) => (
              <div key={`question-#${questionIndex}`}>
                <RhTextArea
                  value={questionAndAnswer.question || ''}
                  mode={RhInputMode.subtle}
                  style={{ backgroundColor: '#fff' }}
                  placeholder="Type a question..."
                  onChange={e => {
                    const newQuestionAndAnswers = [...(props.moduleForm.questionAndAnswers || [])].map((q, i) =>
                      questionIndex === i ? { ...q, question: e.target.value } : q
                    );
                    props.userUpdatedModuleForm({ ...props.moduleForm, questionAndAnswers: newQuestionAndAnswers });
                  }}
                />
                <SizedBox height={8} />
                <SpaceX8>
                  <RhInput
                    mode={RhInputMode.subtle}
                    placeholder="Type an answer..."
                    containerStyle={{ flex: 1 }}
                    style={{ backgroundColor: '#fff' }}
                    value={questionAndAnswer.answer || ''}
                    onChange={e => {
                      const newQuestionAndAnswers = [...(props.moduleForm.questionAndAnswers || [])].map((q, i) =>
                        questionIndex === i ? { ...q, answer: e.target.value } : q
                      );
                      props.userUpdatedModuleForm({ ...props.moduleForm, questionAndAnswers: newQuestionAndAnswers });
                    }}
                  />
                  {(props.moduleForm.questionAndAnswers?.length || 0) > 1 && (
                    <RhButton
                      mode={RhButtonMode.subtle}
                      prefixIcon={FiTrash2}
                      iconSize={18}
                      onClick={() => {
                        const newQuestionsAndAnswers = [...(props.moduleForm.questionAndAnswers || [])];
                        newQuestionsAndAnswers.splice(questionIndex, 1);
                        props.userUpdatedModuleForm({
                          ...props.moduleForm,
                          questionAndAnswers: newQuestionsAndAnswers,
                        });
                      }}
                    />
                  )}
                </SpaceX8>
              </div>
            ))}
          </Questions>
          <SizedBox height={26} />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <H2>Add Scripture</H2>
            <input
              type="checkbox"
              checked={props.moduleForm.scriptureVisible}
              onChange={() => {
                props.userUpdatedModuleForm({
                  ...props.moduleForm,
                  scriptureVisible: !props.moduleForm.scriptureVisible,
                  translationId: null,
                  scriptures: [{ id: '1', scriptureIds: [] }],
                });
              }}
            />
          </div>
          {props.moduleForm.scriptureVisible && renderScriptureModuleForm()}
        </>
      )}
      {type !== 'scripture' && type !== 'qanda' && (
        <>
          {!!props.moduleForm.data ? (
            <ModuleFormFile>
              <RhButton
                mode={RhButtonMode.subtle}
                prefixIcon={FiTrash2}
                iconSize={20}
                onClick={props.userClickedDeleteModuleFormFile}
                disabled={props.isLoading || props.isModuleFormFileUploading}
              />
              {!!props.moduleFormError ? (
                <Description color={colors.error}>{props.moduleFormError}</Description>
              ) : (
                <Description>
                  {props.moduleForm.data.name}
                  {props.isModuleFormFileUploading && ' (Uploading...)'}
                </Description>
              )}
            </ModuleFormFile>
          ) : (
            !!props.moduleForm.url && (
              <ModuleFormFile>
                <RhButton
                  mode={RhButtonMode.subtle}
                  prefixIcon={FiTrash2}
                  iconSize={20}
                  onClick={props.userClickedDeleteModuleFormFile}
                  disabled={props.isLoading || props.isModuleFormFileUploading}
                />
                <Description>{props.moduleForm.url}</Description>
              </ModuleFormFile>
            )
          )}
        </>
      )}
      {type === 'text' && (
        <>
          <SizedBox height={20} />
          <RhTextArea
            mode={RhInputMode.subtle}
            placeholder="Content..."
            containerStyle={{ flex: 1 }}
            style={{ backgroundColor: '#fff', height: 150 }}
            value={props.moduleForm.content || ''}
            onChange={e => {
              props.userUpdatedModuleForm({ ...props.moduleForm, content: e.target.value });
            }}
          />
        </>
      )}
      <ModuleFooterButtons>
        <RhButton
          mode={RhButtonMode.text}
          title="Cancel"
          style={styles.moduleCancel}
          onClick={() => {
            props.userToggledModuleModal();
            props.userResetModuleForm();
          }}
        />
        <RhButton
          mode={RhButtonMode.text}
          title="OK"
          style={styles.moduleOk}
          onClick={props.userSubmittedModuleForm}
          disabled={!!props.moduleFormError || props.isModuleFormFileUploading || props.isLoading}
        />
      </ModuleFooterButtons>
    </RhModal>
  );
};

const styles = {
  moduleCancel: { fontSize: 16, fontWeight: 'normal' } as React.CSSProperties,
  moduleOk: { fontSize: 16, fontWeight: 900 },
  moduleFormError: { marginTop: 30 },
};

const ModuleButtons = styled.div`
  display: flex;
  & > :not(:last-child) {
    margin-right: 20px;
  }
`;

const ModuleFooterButtons = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  margin-top: 50px;
  & > :not(:last-child) {
    margin-right: 24px;
  }
`;

const ModuleFormFile = styled.div`
  margin-top: 24px;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 10px;
  }
`;

const Questions = styled.div`
  & > :not(:last-child) {
    margin-bottom: 24px;
  }
`;

const Scriptures = styled.div`
  & > :not(:last-child) {
    margin-bottom: 35px;
  }
`;

const AlignRight = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const SpaceX8 = styled.div`
  display: flex;
  align-items: center;
  & > :not(:last-child) {
    margin-right: 8px;
  }
`;
